import { Button, Col, Row } from "antd";
import {
  BitrixInfoItemConfig,
  Data,
  InteractiveBlockType,
  Scenario as ScenarioType,
} from "api/dialog";
import InteractiveBlocks from "components/DialogBox/components/Interactive";
import {
  ChangeValue,
  DialogBox,
  DialogHistoryItem,
  NullableChangeValue,
} from "components/DialogBox";
import Back from "components/DialogBox/components/Interactive/components/Back";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { emitEvent, useMobileQuery } from "utils/utils";
import Logo from "components/Logo";
import { parsePath, setPath } from "components/Product";
import {
  Context,
  InitialState,
  isInitialStack,
  useBitrixChat,
  useScenario,
} from "./utils";
import { evalExprStr } from "utils/evaluator";
import { bitrixLeadFromDeal } from "api/hooks";
import i18n from "i18next";
import { useLatest, useMeasure, usePrevious, useWindowSize } from "react-use";
import styles from "./Scenario.module.scss";
import dialogStyles from "components/DialogBox/Dialog.module.scss";
import classNames from "classnames";
import OneClickIcon from "resources/OneClickOrder.svg?react";
import Flex from "components/Flex";

// TODO: decompose into multiple files

const customFooterInteractiveBlockTypes: InteractiveBlockType[] = [
  "authorize",
  "bitrixChat",
];

export interface ScenarioProps {
  title?: any;
  contrastGrayscale?: number;
  scenario: ScenarioType;
  data?: Data;
  bitrixConfig?: BitrixInfoItemConfig[][];
  initialState?: InitialState;
  timeout?: Parameters<typeof useScenario>[1];
  onChange?: (value: ChangeValue<unknown> | null, context: Context) => void;
  onStackChange: (stack: [branch: string, state: number, context: Context][]) => void;
}
export function DialogBoxFooter({
  mode = "initial",
  contrastGrayscale,
  onBack,
  onChat,
}: {
  contrastGrayscale?: number;
  mode?: "initial" | "default" | "noBack" | "chat" | "none";
  onBack?: () => void;
  onChat?: (mode?: "initial" | "default" | "chat" | "none") => void;
}) {
  if (mode === "initial")
    return (
      <Flex.Row style={{ marginTop: "16px" }} align="center" justify="space-between">
        <Flex.Col>
          <Back
            onClick={() => window.history.back()}
            contrastGrayscale={contrastGrayscale}
          />
        </Flex.Col>
        <Flex.Col>
          <Button
            type="text"
            className={styles.orderInOneClick}
            onClick={() => onChat?.(mode)}
          >
            {i18n.t("widget.oneClickOrder.buttonName")}
            <OneClickIcon />
          </Button>
        </Flex.Col>
      </Flex.Row>
    );
  if (mode === "default")
    return (
      <Row style={{ marginTop: "24px" }} align="middle">
        <Col flex={1}>
          {onBack && <Back onClick={onBack} contrastGrayscale={contrastGrayscale} />}
        </Col>
      </Row>
    );
  if (mode === "chat")
    return (
      <Row style={{ marginTop: "24px" }} align="middle">
        <Col flex={1}>
          {onBack && (
            <Back
              contrastGrayscale={contrastGrayscale}
              onClick={onBack}
              text={i18n.t("widget.bitrix.finishManagerChat")}
            />
          )}
        </Col>
      </Row>
    );
  return <></>;
}

type OnChangeArgs = [
  value?: NullableChangeValue<unknown>,
  ...display: DialogHistoryItem[]
];

export const DIALOG_BOX_PORTAL_ID = "dialogBoxPortalId";

// TODO: move content box index into scenario hook and expose changes through callback
export const Scenario = ({
  title,
  scenario: branches = { initial: [] },
  initialState,
  contrastGrayscale,
  bitrixConfig = [],
  timeout = { delay: Number.POSITIVE_INFINITY, callback: () => {} },
  onChange,
  onStackChange,
}: ScenarioProps) => {
  const [trigger, rerender] = useState([]);
  const mobile = useMobileQuery();
  const scrollbox = useRef<HTMLDivElement>(null);
  const _scrollboxBodyRef = useRef<HTMLDivElement>(null);
  const [scrollboxBodyRef, scrollboxMeasure] = useMeasure<HTMLDivElement>();
  const [keyboardBodyRef, keyboardMeasure] = useMeasure<HTMLDivElement>();
  const windowMeasure = useWindowSize();
  const scrollToBottom = useCallback(() => {
    if (!scrollbox.current) return;
    scrollbox.current.scrollTop = scrollbox.current.scrollHeight;
  }, []);
  const bitrix = useBitrixChat(bitrixConfig);
  const { state, ...methods } = useScenario(branches, timeout, initialState);
  const { pushStack, back, showMessage, nextStep, setContext, setData, clearCallStack } =
    methods;
  let { context, data, stack, dialog, scenarioState, branch } = state;
  const prevStack = usePrevious(stack);
  const latestData = useLatest(data);
  const latestContext = useLatest(context);

  useEffect(() => {
    const stackWasEmpty =
      !prevStack || (prevStack.length === 1 && prevStack[0].stack.length === 0);
    if (stackWasEmpty && stack[0].stack.length > 0) emitEvent("widgetOpenClick");
  }, [prevStack, stack]);

  useEffect(
    () => bitrix.setUserData({ ...data, ctx: context }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [context, data]
  );

  useEffect(() => {
    onStackChange?.([
      ...stack.flatMap(({ branch, stack }) =>
        stack[stack.length - 1]
          ? [[branch, stack[stack.length - 1].state, stack[stack.length - 1].context]]
          : []
      ),
      [branch, scenarioState, context],
    ] as [string, number, Context][]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch, scenarioState, stack]);

  const onChat = useCallback(
    (mode?: "initial" | "default" | "chat" | "none") => {
      if (mode === "initial") {
        _onChange(
          { next: "oneClickOrder" },
          {
            type: "text",
            dialogBlockType: "user",
            text: i18n.t("widget.oneClickOrder.buttonName"),
          }
        );
        bitrixLeadFromDeal({
          TITLE: "Покупка в 1 клик",
          CATEGORY_ID: "0",
          TYPE_ID: "SALE",
        })(context.contact as any);
        return;
      }
      if (bitrix.chatState === "pending" || bitrix.chatState === "uninitialized")
        bitrix.start();

      bitrix.toggle();
      if (mode === "default")
        bitrixLeadFromDeal({
          TITLE: "Написать менеджеру",
          CATEGORY_ID: "0",
          TYPE_ID: (context.TYPE_ID ?? "SALE") as string,
        })(context.contact as any);
      rerender([]);
    },
    [bitrix, context.TYPE_ID, context.contact]
  );
  const onBack = useCallback(() => {
    if (bitrix.showDialog) {
      bitrix.toggle();
      rerender([]);
      return;
    }

    back();
    rerender([]);
  }, [back, bitrix]);

  const _onChange = useCallback(
    (...args: OnChangeArgs) => {
      const [value, ...messages] = args;
      if (!value) {
        scrollToBottom();
        return;
      }
      const {
        progressScenario = true,
        pushStack: shouldPushStack = true,
        resetProduct,
      } = value;

      if (resetProduct) {
        onChange?.(value, context);
        rerender([]);
        return;
      }

      let rerenderKeyboard = value.rerenderKeyboard ?? progressScenario;

      if (value.clearCallStack) clearCallStack();

      setTimeout(() => {
        showMessage({ ...latestData.current, ctx: latestContext.current }, ...messages);
      });

      if (!progressScenario) pushStack(shouldPushStack);
      else rerenderKeyboard &&= nextStep(shouldPushStack, value?.next);

      if (value.name && value.path)
        setContext((context) => {
          const next = setPath(context, ["answers", value.path as string], {
            name: value.name,
            block: value.block,
          });
          return next;
        });

      if (value.path?.startsWith("ctx"))
        setContext((context) => {
          const [, ...path] = parsePath(value.path as string, context);
          const next = setPath(context, path, value.value);
          return next;
        });
      else if (value && value.path) {
        const path = parsePath(value.path, { ctx: context, ...data });
        const newData = setPath(data, path, value.value);

        setData(newData);
        onChange?.(value, context);
      }

      if (rerenderKeyboard) rerender([]);

      scrollToBottom();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dialog, branch, state, nextStep]
  );

  const _keyboard = branches[branch][scenarioState]?.dialogBlock;
  const keyboard = useMemo(() => {
    if (bitrix.showDialog) {
      // if (!bitrix.chatStarted) return <>Оператор отключился</>;
      // if (!bitrix.chatStarted) return <></>;
      const Keyboard = InteractiveBlocks({
        type: "input",
        blockName: "ctx.chatMessage",
        placeholder: i18n.t("placeholder.enterMessage"),
        required: true,
      });

      return (
        <div className={classNames(dialogStyles.input, dialogStyles.block)}>
          <Keyboard
            contrastGrayscale={contrastGrayscale}
            mobile={mobile}
            data={data}
            bitrix={bitrix}
            context={context}
            className={dialogStyles.keyboardInner}
            onChange={(...[arg]) => (arg ? bitrix.send(arg.value as string) : void {})}
          />
        </div>
      );
    }

    if (!_keyboard) return <></>;

    const Keyboard = InteractiveBlocks(_keyboard);
    return (
      <div className={classNames(dialogStyles[_keyboard.type], dialogStyles.block)}>
        <Keyboard
          contrastGrayscale={contrastGrayscale}
          mobile={mobile}
          data={data}
          bitrix={bitrix}
          context={context}
          className={dialogStyles.keyboardInner}
          onChange={_onChange}
          onBack={bitrix.showDialog || !isInitialStack(stack) ? onBack : undefined}
        />
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  const [scrollboxScroll, setScrollboxScroll] = useState(0);
  useEffect(
    function () {
      if (!scrollbox.current) return;
      const ref = scrollbox.current;
      const handler = function () {
        setScrollboxScroll(ref.scrollTop ?? 0);
      };

      ref.addEventListener("scroll", handler, {
        capture: false,
        passive: true,
      });
      return function () {
        ref.removeEventListener("scroll", handler);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scrollbox.current]
  );
  const background = useMemo(
    () => (
      <div
        className={dialogStyles.background}
        style={{
          width: scrollboxMeasure.width,
          left: scrollbox.current?.offsetLeft ?? 0,
          top: Math.min(
            (scrollbox.current?.offsetTop ?? 0) + scrollboxMeasure.top - scrollboxScroll,
            windowMeasure.height - keyboardMeasure.height - 32 - 24
          ),
          height: 280 + 32 + scrollboxMeasure.height + keyboardMeasure.height,
        }}
      />
    ),
    [
      keyboardMeasure.height,
      scrollboxMeasure.height,
      scrollboxMeasure.top,
      scrollboxMeasure.width,
      scrollboxScroll,
      windowMeasure.height,
    ]
  );
  const footer = useMemo(() => {
    const kb = branches[branch][scenarioState]?.dialogBlock ?? {};
    const notCustomFooter = !customFooterInteractiveBlockTypes.includes(
      kb.type as InteractiveBlockType
    );
    const noBack =
      ("disableBack" in kb &&
        kb.disableBack &&
        evalExprStr(kb.disableBack, {
          ...data,
          data,
          ctx: context,
        })) ||
      kb.type === "signContract";
    const mode = (() => {
      if (bitrix.showDialog) return "chat";
      if (isInitialStack(stack)) return "initial";
      if (notCustomFooter) return "default";
      return "none";
    })();
    return (
      <DialogBoxFooter
        contrastGrayscale={contrastGrayscale}
        mode={mode}
        onBack={!noBack ? onBack : undefined}
        onChat={!bitrix.showDialog ? onChat : undefined}
      />
    );
  }, [
    bitrix.showDialog,
    branch,
    branches,
    context,
    contrastGrayscale,
    data,
    onBack,
    onChat,
    scenarioState,
    stack,
  ]);
  const dialogBlock = useMemo(
    () => {
      return (
        <div className={dialogStyles.blocks}>
          <div
            style={{
              textAlign: "left",
              paddingBottom: 16,
              paddingTop: 48,
            }}
          >
            <Logo />
          </div>
          <div style={{ display: bitrix.showDialog ? "none" : undefined }}>
            <DialogBox dialog={dialog} onChange={scrollToBottom} />
          </div>
          <div style={{ display: !bitrix.showDialog ? "none" : undefined }}>
            <DialogBox html dialog={bitrix.dialog} onChange={scrollToBottom} />
          </div>
          <div id={DIALOG_BOX_PORTAL_ID} style={{ marginTop: 8 }} />
        </div>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bitrix.dialog, bitrix.showDialog, dialog]
  );

  return (
    <>
      {!mobile && background}
      <div ref={scrollbox} className={styles.scrollbox}>
        <div
          ref={(ref) => {
            // @ts-ignore
            _scrollboxBodyRef.current = ref;
            if (!ref) return;
            scrollboxBodyRef(ref);
          }}
          id="scrollbox"
          className={styles.scrollbox}
          style={{
            display: "flex",
            flexDirection: "column-reverse",
            minHeight: "100%",
            position: "relative",
            padding: mobile ? "24px" : undefined,
            marginTop: mobile ? undefined : 48,
          }}
        >
          <div ref={keyboardBodyRef} className={styles.keyboard}>
            {keyboard}
            {footer}
          </div>
          {dialogBlock}
          {title}
        </div>
      </div>
    </>
  );
};
